import React, { useState, useEffect, useRef, useCallback } from "react";
import Modal from "react-modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTrash, faShoppingCart } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import Swal from "sweetalert2";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "sweetalert2/src/sweetalert2.scss";
import "../App.css";
import {
  checkPhoneclient,
  getToken,
  ExecApiDetailOrderWhatsap,
  insertTemporaryPedidos,
} from "./api";
import {
  customStyles,
  StyledInput,
  MessageAlertTokenExpire,
} from "../customStyles";
import { calculateCartTotal } from "./cartTotal";

Modal.setAppElement("#root");

const formatCOP = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const ProductMenu = ({ products, categories, modifier_groups }) => {
  const [isChecked, setIsChecked] = useState({});
  const [cart, setCart] = useState([]);
  const [acompanantesOptions, setAcompanantesOptions] = useState([]);
  const REDIRECT_WHATSAPP = process.env.REACT_APP_REDIRECT_WHATSAPP;
  const [modalIsOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAcompanantes, setSelectedAcompanantes] = useState({});
  const [selectedQuantities, setSelectedQuantities] = useState({});
  const [selectedQuantitiesV2, setSelectedQuantitiesV2] = useState({});

  const handleAcompananteChange = (itemId, acompananteId) => {
    setSelectedAcompanantes((prev) => ({
      ...prev,
      [itemId]: acompananteId,
    }));
  };

  const total = calculateCartTotal(cart, selectedQuantities, products);
  const [selections, setSelections] = useState({});
  const [allOptionsSelected, setAllOptionsSelected] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");

  // Category bar scroll logic
  const categoryBarRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = categoryBarRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 5);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 5);
  }, []);

  useEffect(() => {
    const el = categoryBarRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [categories, updateScrollState]);

  const scrollCategories = (direction) => {
    const el = categoryBarRef.current;
    if (!el) return;
    const scrollAmount = 200;
    el.scrollBy({ left: direction === "left" ? -scrollAmount : scrollAmount, behavior: "smooth" });
  };

  const itemIdToCategoryName = {};
  categories.forEach((category) => {
    category.app_item_ids.forEach((itemId) => {
      itemIdToCategoryName[itemId] = category.category_name;
    });
  });

  const filteredProducts = products.filter(
    (product) =>
      (selectedCategory === "" ||
        itemIdToCategoryName[product.app_item_id] === selectedCategory) &&
      product.item_name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      product.is_sold_separately === true
  );

  useEffect(() => {
    if (selectedProduct) {
      const modifierGroups = modifier_groups
        ? modifier_groups.filter((group) =>
          selectedProduct.app_modifier_group_ids.includes(
            group.app_modifier_group_id
          )
        )
        : [];
      const newAcompanantesOptions = modifierGroups.flatMap(
        (group) => group.app_mg_items
      );
      setAcompanantesOptions(newAcompanantesOptions);
    }
  }, [modifier_groups, selectedProduct]);

  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const addToCart = (productId, quantity) => {
    setIsAddingToCart(true);
    setTimeout(() => setIsAddingToCart(false), 1500);
    const existingProductIndex = cart.findIndex(
      (product) => product.app_item_id === productId
    );
    if (existingProductIndex >= 0) {
      const updatedCart = [...cart];
      updatedCart[existingProductIndex].quantity += quantity;
      setCart(updatedCart);
    } else {
      const selectedProduct = products.find(
        (product) => product.app_item_id === productId
      );
      const modifierGroup = modifier_groups
        ? modifier_groups.find((group) =>
          selectedProduct.app_modifier_group_ids.includes(
            group.app_modifier_group_id
          )
        )
        : null;
      const newProductWithModifierGroup = {
        ...selectedProduct,
        quantity,
        acompanantes: obtenerAcompanantesParaProducto(
          selectedProduct,
          products,
          modifier_groups
        ),
        modifierGroupId: modifierGroup
          ? modifierGroup.app_modifier_group_id
          : null,
      };
      setCart([...cart, newProductWithModifierGroup]);
      const newAcompanantesOptions = modifierGroup
        ? modifierGroup.app_mg_items
        : [];
      setAcompanantesOptions(newAcompanantesOptions);
    }
    setShouldBlink(true);
    setTimeout(() => setShouldBlink(false), 2000);

    // Success toast
    Swal.fire({
      icon: "success",
      title: "¡Añadido!",
      showConfirmButton: false,
      timer: 800,
      toast: true,
      position: "top-end",
      customClass: { popup: "swal2-toast-custom" },
    });
  };

  const obtenerAcompanantesParaProducto = (
    producto,
    items,
    modifier_groups
  ) => {
    if (!Array.isArray(modifier_groups)) {
      return [];
    }
    const gruposModificadores = modifier_groups.filter((group) =>
      producto.app_modifier_group_ids.includes(group.app_modifier_group_id)
    );
    if (!gruposModificadores.length) return [];
    let acompanantes = [];
    gruposModificadores.forEach((grupo) => {
      const acompanantesGrupo = items.filter((item) =>
        grupo.app_mg_items.some(
          (mg_item) => mg_item.app_item_id === item.app_item_id
        )
      );
      acompanantes = [...acompanantes, ...acompanantesGrupo];
    });
    return acompanantes;
  };

  let isQuantityValid = true;

  const InitOrderTemporary = async () => {
    if (total <= 15000) {
      MessageValorTotal();
      return;
    }

    const requiredGroups = cart.filter((item) =>
      item.modifier_groups.some(
        (group) =>
          group.is_required === 1 &&
          !selections[
          `group-${item.app_item_id}-${group.app_modifier_group_id}`
          ]
      )
    );
    if (requiredGroups.length > 0) {
      setError(true);
      return;
    }

    cart.forEach((item) => {
      item.modifier_groups.forEach((group) => {
        if (group.is_required === 3) {
          const idPrefix = `${item.app_item_id}-`;
          const selectedQuantitiesForItem = Object.entries(selectedQuantitiesV2)
            .filter(([key]) => key.startsWith(idPrefix))
            .reduce(
              (obj, [key, value]) => ({
                ...obj,
                [key]: value,
              }),
              {}
            );
          const totalQuantity = Object.values(selectedQuantitiesForItem).reduce(
            (total, quantity) => total + quantity,
            0
          );
          const isGroupValid = totalQuantity === group.quantity_max_permitted;
          if (!isGroupValid) {
            isQuantityValid = false;
          }
        }
      });
    });

    if (!isQuantityValid) {
      setError(true);
      return;
    }

    MessageAlertCheckWhatsap();

    try {
      const selectedProducts = cart.map((item) => ({
        app_item_id: item.app_item_id,
        name: item.item_name,
        price: item.price,
        quantity: item.quantity,
      }));

      const item_pedidos_ids = cart.map((item) => {
        let sub_item_list = [];
        if (Array.isArray(item.modifier_groups)) {
          item.modifier_groups.forEach((group) => {
            if (Array.isArray(group.app_mg_items)) {
              group.app_mg_items.forEach((acompanante) => {
                if (
                  selectedQuantities[
                  `${item.app_item_id}-${acompanante.app_item_id}`
                  ] > 0
                ) {
                  const acompananteNombre = item.acompanantes.find(
                    (a) => a.app_item_id === acompanante.app_item_id
                  );
                  sub_item_list.push({
                    app_item_id: acompanante.app_item_id,
                    name_modifier_groups: acompananteNombre
                      ? acompananteNombre.item_name
                      : "Acompañante no encontrado",
                    quantity:
                      selectedQuantities[
                      `${item.app_item_id}-${acompanante.app_item_id}`
                      ],
                    price: acompananteNombre.price,
                  });
                }
              });
            }
          });
        }
        if (selectedRadio) {
          Object.entries(selectedRadio).forEach(
            ([groupName, groupSelections]) => {
              Object.keys(groupSelections).forEach((uniqueId) => {
                const [productId, acompananteId] = uniqueId.split("-");
                if (productId === item.app_item_id) {
                  if (groupSelections[uniqueId]) {
                    const acompananteSeleccionado = item.acompanantes.find(
                      (a) => a.app_item_id === acompananteId
                    );
                    if (acompananteSeleccionado) {
                      sub_item_list.push({
                        app_item_id: acompananteId,
                        name_modifier_groups:
                          acompananteSeleccionado.item_name,
                        price: acompananteSeleccionado.price,
                        quantity: 1,
                      });
                    }
                  }
                }
              });
            }
          );
        }
        return {
          app_item_id: item.app_item_id,
          quantity: item.quantity,
          sub_item_list: sub_item_list,
        };
      });

      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get("token");
      await insertTemporaryPedidos(item_pedidos_ids, tokenFromUrl, total);
      const phone = await checkPhoneclient(tokenFromUrl);
      if (!phone) {
        MessageAlertTokenExpire();
      }
      await ExecApiDetailOrderWhatsap(
        tokenFromUrl,
        selectedProducts,
        phone,
        item_pedidos_ids,
        total
      );
      closeModal();
      window.location.href = REDIRECT_WHATSAPP;
    } catch (error) {
      console.error("Error al procesar la solicitud:", error);
    }
  };

  const MessageValorTotal = () => {
    Swal.fire({
      icon: "warning",
      title: "Valor mínimo no alcanzado",
      text: "El valor del pedido debe ser mayor de $15.000",
      confirmButtonText: "Ok",
      confirmButtonColor: "#e63946",
    });
  };

  const MessageAlertCheckWhatsap = () => {
    Swal.fire({
      icon: "success",
      title: "Revise la bandeja de mensajería de WhatsApp",
      html: '<div class="progress-bar-container"><div class="progress-bar"></div></div>',
      showConfirmButton: true,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
  };

  const removeFromCart = (productId) => {
    const updatedCart = cart.filter((item) => item.app_item_id !== productId);
    setCart(updatedCart);
  };

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  const formattedTotal = formatCOP(total);

  const [shouldBlink, setShouldBlink] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 12;
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const pageNumbers = [];
  for (
    let i = 1;
    i <= Math.ceil(filteredProducts.length / productsPerPage);
    i++
  ) {
    pageNumbers.push(i);
  }

  const [error, setError] = useState(false);
  const [selectedRadio, setSelectedRadio] = useState([]);

  const handleCheckboxChange = (event, quantity_max_permitted, uniqueId) => {
    const selectedCount = Object.values(isChecked).filter(Boolean).length;
    if (
      quantity_max_permitted &&
      selectedCount >= quantity_max_permitted &&
      !isChecked[uniqueId]
    ) {
      return;
    }
    setIsChecked({
      ...isChecked,
      [uniqueId]: event.target.checked,
    });
    if (event.target.checked) {
      setSelectedQuantities({
        ...selectedQuantities,
        [uniqueId]: 1,
      });
    } else {
      setSelectedQuantities({
        ...selectedQuantities,
        [uniqueId]: 0,
      });
    }
    if (event.target.checked) {
      setTotalSelected(totalSelected + 1);
    } else {
      setTotalSelected(totalSelected - 1);
    }
  };

  const [totalSelected, setTotalSelected] = useState(0);

  const handleCheckboxChangev2 = (
    event,
    quantity_max_permitted,
    uniqueId,
    item
  ) => {
    const idPrefix = uniqueId.split("-")[0];
    const selectedCount = Object.entries(selectedQuantities)
      .filter(([key]) => key.startsWith(idPrefix))
      .reduce((total, [, value]) => total + value, 0);

    setIsChecked({
      ...isChecked,
      [uniqueId]: event.target.checked,
    });
    if (event.target.checked) {
      setSelectedQuantities((prevQuantities) => ({
        ...prevQuantities,
        [uniqueId]: (prevQuantities[uniqueId] || 0) + 1,
      }));
      setSelectedQuantitiesV2((prevQuantities) => ({
        ...prevQuantities,
        [uniqueId]: (prevQuantities[uniqueId] || 0) + 1,
      }));
    } else {
      setSelectedQuantities((prevQuantities) => ({
        ...prevQuantities,
        [uniqueId]: 0,
      }));
      setSelectedQuantitiesV2((prevQuantities) => ({
        ...prevQuantities,
        [uniqueId]: 0,
      }));
      setIsChecked((prevChecked) => ({
        ...prevChecked,
        [uniqueId]: false,
      }));
    }
    if (event.target.checked) {
      setTotalSelected(totalSelected + 1);
    } else {
      setTotalSelected(totalSelected - 1);
    }
    const requiredGroups = cart.filter((item) =>
      item.modifier_groups.some(
        (group) =>
          group.is_required === 1 && !selections[group.app_modifier_group_id]
      )
    );
    setError(requiredGroups.length > 0);
  };

  const incrementAdicionesV2 = (productId, quantity_max_permitted) => {
    const idPrefix = productId.split("-")[0];
    const totalQuantity = Object.entries(selectedQuantitiesV2)
      .filter(([key]) => key.startsWith(idPrefix))
      .reduce((total, [, value]) => total + value, 0);
    if (totalQuantity < quantity_max_permitted) {
      setSelectedQuantities((prevQuantities) => ({
        ...prevQuantities,
        [productId]: (prevQuantities[productId] || 0) + 1,
      }));
      setSelectedQuantitiesV2((prevQuantities) => ({
        ...prevQuantities,
        [productId]: (prevQuantities[productId] || 0) + 1,
      }));
    }
  };

  const decrementAdicionesV2 = (productId) => {
    setSelectedQuantities((prevQuantities) => {
      const currentQuantity = prevQuantities[productId] || 0;
      if (currentQuantity > 0) {
        const newQuantity = currentQuantity - 1;
        if (newQuantity === 0) {
          setIsChecked((prevChecked) => ({
            ...prevChecked,
            [productId]: false,
          }));
        }
        return { ...prevQuantities, [productId]: newQuantity };
      }
      return prevQuantities;
    });
    setSelectedQuantitiesV2((prevQuantities) => {
      const currentQuantity = prevQuantities[productId] || 0;
      if (currentQuantity > 0) {
        const newQuantity = currentQuantity - 1;
        if (newQuantity === 0) {
          setIsChecked((prevChecked) => ({
            ...prevChecked,
            [productId]: false,
          }));
        }
        return { ...prevQuantities, [productId]: newQuantity };
      }
      return prevQuantities;
    });
  };

  useEffect(() => {
    for (const [productId, quantity] of Object.entries(selectedQuantities)) {
      if (quantity === 0) {
        setIsChecked((prevChecked) => ({
          ...prevChecked,
          [productId]: false,
        }));
      }
    }
  }, [selectedQuantities]);

  useEffect(() => {
    for (const [productId, quantity] of Object.entries(selectedQuantitiesV2)) {
      if (quantity === 0) {
        setIsChecked((prevChecked) => ({
          ...prevChecked,
          [productId]: false,
        }));
      }
    }
  }, [selectedQuantitiesV2]);

  const incrementQuantity = (productId) => {
    setSelectedQuantities({
      ...selectedQuantities,
      [productId]: (selectedQuantities[productId] || 1) + 1,
    });
  };

  const decrementQuantity = (productId) => {
    const currentQuantity = selectedQuantities[productId] || 1;
    if (currentQuantity > 1) {
      setSelectedQuantities({
        ...selectedQuantities,
        [productId]: currentQuantity - 1,
      });
    }
  };

  const incrementAdiciones = (productId, quantity_max_permitted) => {
    const currentQuantity = selectedQuantities[productId] || 1;
    if (currentQuantity < quantity_max_permitted) {
      setSelectedQuantities({
        ...selectedQuantities,
        [productId]: currentQuantity + 1,
      });
    }
  };

  const decrementAdiciones = (productId) => {
    const currentQuantity = selectedQuantities[productId] || 0;
    if (currentQuantity > 1) {
      setSelectedQuantities({
        ...selectedQuantities,
        [productId]: currentQuantity - 1,
      });
    }
  };

  const [expanded, setExpanded] = useState(false);
  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleRadioChange = (item, group) => {
    setSelections((prevSelections) => {
      const groupName = `group-${item.app_item_id}-${group.app_modifier_group_id}`;
      if (
        !prevSelections[groupName] ||
        prevSelections[groupName] !== item.app_item_id
      ) {
        return {
          ...prevSelections,
          [groupName]: item.app_item_id,
        };
      }
      return prevSelections;
    });
  };

  const [areConditionsMet, setAreConditionsMet] = useState(false);

  // Reset page when category or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchTerm]);

  return (
    <React.Fragment>
      {/* ===== Category Pills Bar ===== */}
      <div className="category-bar-wrapper">
        <button
          className={`category-scroll-btn left ${!canScrollLeft ? "hidden" : ""}`}
          onClick={() => scrollCategories("left")}
          aria-label="Scroll categorías izquierda"
        >
          ‹
        </button>
        <div className="category-bar" ref={categoryBarRef}>
          <button
            className={`category-pill ${selectedCategory === "" ? "active" : ""}`}
            onClick={() => setSelectedCategory("")}
          >
            🍽️ Todos
          </button>
          {categories.map((category) => (
            <button
              key={category.category_name}
              className={`category-pill ${selectedCategory === category.category_name ? "active" : ""
                }`}
              onClick={() => setSelectedCategory(category.category_name)}
            >
              {category.category_name}
            </button>
          ))}
        </div>
        <button
          className={`category-scroll-btn right ${!canScrollRight ? "hidden" : ""}`}
          onClick={() => scrollCategories("right")}
          aria-label="Scroll categorías derecha"
        >
          ›
        </button>
      </div>

      {/* ===== Floating Cart Button ===== */}
      <div className="sidebar">
        <div className={`ex-fdlist ${isAddingToCart ? "cart-fly" : ""}`}>
          <div
            className={`exfd-shopping-cart ${shouldBlink ? "blink" : ""}`}
            onClick={openModal}
          >
            <img
              decoding="async"
              src="/carritokokoriko.svg"
              alt="Carrito"
            />
            <span className="exfd-cart-num">{cart.length}</span>
          </div>
        </div>
      </div>

      {/* ===== Search Bar ===== */}
      <div className="search-section">
        <FontAwesomeIcon icon={faSearch} className="search-icon" />
        <input
          type="text"
          placeholder="Buscar productos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* ===== Product Grid ===== */}
      <div className="product-menu">
        <div className="products-section">
          {currentProducts.map((product, index) => (
            <div key={product.app_item_id} className="product-item">
              {/* Product Image */}
              <div className="product-image-wrapper">
                <img
                  src={product.head_img || ""}
                  alt={product.item_name}
                  loading={index < 4 ? "eager" : "lazy"}
                  decoding="async"
                  fetchPriority={index < 4 ? "high" : "auto"}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>

              {/* Product Info */}
              <div className="product-info">
                <h3>{product.item_name}</h3>
                {product.short_desc && (
                  <p className="product-description">{product.short_desc}</p>
                )}
                <p className="product-price">{formatCOP(product.price)}</p>
              </div>

              {/* Product Actions */}
              <div className="product-actions">
                <div className="quantity-stepper">
                  <button
                    onClick={() => decrementQuantity(product.app_item_id)}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    value={selectedQuantities[product.app_item_id] || 1}
                    onChange={(e) =>
                      setSelectedQuantities({
                        ...selectedQuantities,
                        [product.app_item_id]: parseInt(e.target.value),
                      })
                    }
                  />
                  <button
                    onClick={() => incrementQuantity(product.app_item_id)}
                  >
                    +
                  </button>
                </div>
                <button
                  className="add-to-cart"
                  onClick={() =>
                    addToCart(
                      product.app_item_id,
                      selectedQuantities[product.app_item_id] || 1
                    )
                  }
                >
                  Añadir
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* ===== Cart Modal ===== */}
        <Modal
          isOpen={modalIsOpen}
          onRequestClose={closeModal}
          style={customStyles}
          contentLabel="Carrito de compras"
        >
          <button onClick={closeModal} className="button-volver">
            ← Volver al menú
          </button>

          {cart.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 0", color: "var(--color-text-light)" }}>
              <p style={{ fontSize: "var(--font-size-lg)", fontWeight: 500 }}>
                Tu carrito está vacío
              </p>
              <p style={{ fontSize: "var(--font-size-sm)", marginTop: "8px" }}>
                Añade productos desde el catálogo
              </p>
            </div>
          )}

          {cart.map((item, itemIndex) => {
            return (
              <div key={item.app_item_id} className="cart-item">
                <h3>{item.item_name}</h3>
                <p>
                  Precio: {formatCOP(item.price)}
                </p>
                <p>Cantidad: {item.quantity}</p>

                {Array.isArray(item.modifier_groups) &&
                  item.modifier_groups.map((group, groupIndex) => {
                    const panel = `panel${itemIndex}-${groupIndex}`;
                    return (
                      <Accordion
                        expanded={
                          expanded === panel ||
                          group.is_required === 1 ||
                          group.is_required === 3
                        }
                        onChange={handleChange(panel)}
                        key={group.app_modifier_group_id}
                        className="accordion"
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls={`panel${itemIndex}-${groupIndex}bh-content`}
                          id={`panel${itemIndex}-${groupIndex}bh-header`}
                          className="accordion-summary"
                        >
                          <Typography
                            sx={{
                              width: "100%",
                              flexShrink: 0,
                              fontWeight: 600,
                              fontFamily: "var(--font-family)",
                              fontSize: "var(--font-size-sm)",
                            }}
                          >
                            {group.modifier_group_name}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails className="accordion-details">
                          {(() => {
                            if (group.is_required === 3) {
                              const idPrefix = `${item.app_item_id}-`;
                              const selectedQuantitiesForItem = Object.entries(
                                selectedQuantities
                              )
                                .filter(([key]) => key.startsWith(idPrefix))
                                .reduce(
                                  (obj, [key, value]) => ({
                                    ...obj,
                                    [key]: value,
                                  }),
                                  {}
                                );
                              const totalQuantity = Object.values(
                                selectedQuantitiesForItem
                              ).reduce(
                                (total, quantity) => total + quantity,
                                0
                              );
                              isQuantityValid =
                                totalQuantity == group.quantity_max_permitted;
                              if (!isQuantityValid) {
                                return (
                                  <div
                                    style={{
                                      color: "var(--color-danger)",
                                      fontSize: "var(--font-size-sm)",
                                      fontWeight: 500,
                                      padding: "8px 12px",
                                      background: "rgba(239, 68, 68, 0.08)",
                                      borderRadius: "8px",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    Debes seleccionar la cantidad requerida de
                                    acompañamientos ({group.quantity_max_permitted}).
                                  </div>
                                );
                              }
                            }
                          })()}
                          {Array.isArray(group.app_mg_items) &&
                            group.app_mg_items.map((bebida) => {
                              const acompanante = item.acompanantes.find(
                                (a) => a.app_item_id === bebida.app_item_id
                              );
                              const product = products.find(
                                (product) =>
                                  product.app_item_id === bebida.app_item_id
                              );
                              const price = product ? product.price : 0;
                              return (
                                <div
                                  key={bebida.app_item_id}
                                  className="cart-opciones"
                                >
                                  {(() => {
                                    switch (group.is_required) {
                                      case 1:
                                        return (
                                          <>
                                            {error && (
                                              <div
                                                style={{
                                                  color: "var(--color-danger)",
                                                  fontSize: "var(--font-size-xs)",
                                                  fontWeight: 500,
                                                }}
                                              >
                                                Selecciona una opción
                                              </div>
                                            )}
                                            <input
                                              className="radio-button"
                                              type="radio"
                                              id={`${item.app_item_id}-${bebida.app_item_id}`}
                                              name={`group-${item.app_item_id}-${group.app_modifier_group_id}`}
                                              value={bebida.app_item_id}
                                              onChange={(e) => {
                                                handleRadioChange(item, group);
                                                setSelectedRadio(
                                                  (prevSelectedRadio) => {
                                                    const groupName = `group-${item.app_item_id}-${group.app_modifier_group_id}`;
                                                    const updatedGroupSelections = {};
                                                    updatedGroupSelections[
                                                      `${item.app_item_id}-${e.target.value}`
                                                    ] = true;
                                                    const updatedSelections = {
                                                      ...prevSelectedRadio,
                                                      [groupName]:
                                                        updatedGroupSelections,
                                                    };
                                                    return updatedSelections;
                                                  }
                                                );
                                              }}
                                            />
                                          </>
                                        );
                                      case 2:
                                        return (
                                          <>
                                            <input
                                              type="checkbox"
                                              id={`${item.app_item_id}-${bebida.app_item_id}`}
                                              name={
                                                group.app_modifier_group_id
                                              }
                                              value={bebida.app_item_id}
                                              onChange={(e) => {
                                                handleCheckboxChange(
                                                  e,
                                                  group.quantity_max_permitted,
                                                  `${item.app_item_id}-${bebida.app_item_id}`
                                                );
                                              }}
                                            />
                                            {isChecked[
                                              `${item.app_item_id}-${bebida.app_item_id}`
                                            ] && (
                                                <div>
                                                  <button
                                                    className="button-size"
                                                    onClick={() =>
                                                      decrementAdiciones(
                                                        `${item.app_item_id}-${bebida.app_item_id}`
                                                      )
                                                    }
                                                  >
                                                    −
                                                  </button>
                                                  <span
                                                    style={{
                                                      fontWeight: 600,
                                                      minWidth: "20px",
                                                      display: "inline-block",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    {selectedQuantities[
                                                      `${item.app_item_id}-${bebida.app_item_id}`
                                                    ] || 1}
                                                  </span>
                                                  <button
                                                    className="button-size"
                                                    onClick={() =>
                                                      incrementAdiciones(
                                                        `${item.app_item_id}-${bebida.app_item_id}`,
                                                        group.quantity_max_permitted
                                                      )
                                                    }
                                                  >
                                                    +
                                                  </button>
                                                </div>
                                              )}
                                          </>
                                        );
                                      case 3:
                                        const idPrefix = `${item.app_item_id}-`;
                                        const selectedQuantitiesForItem =
                                          Object.entries(selectedQuantitiesV2)
                                            .filter(([key]) =>
                                              key.startsWith(idPrefix)
                                            )
                                            .reduce(
                                              (obj, [key, value]) => ({
                                                ...obj,
                                                [key]: value,
                                              }),
                                              {}
                                            );
                                        const totalQuantity = Object.values(
                                          selectedQuantitiesForItem
                                        ).reduce(
                                          (total, quantity) =>
                                            total + quantity,
                                          0
                                        );
                                        isQuantityValid =
                                          totalQuantity ==
                                          group.quantity_max_permitted;
                                        const uniqueId = `${item.app_item_id}-${bebida.app_item_id}`;
                                        return (
                                          <>
                                            <input
                                              type="checkbox"
                                              id={uniqueId}
                                              name={
                                                group.app_modifier_group_id
                                              }
                                              value={bebida.app_item_id}
                                              onChange={(event) => {
                                                handleCheckboxChangev2(
                                                  event,
                                                  group.quantity_max_permitted,
                                                  uniqueId,
                                                  item
                                                );
                                              }}
                                              checked={
                                                isChecked[uniqueId] || false
                                              }
                                              disabled={
                                                totalQuantity >=
                                                group.quantity_max_permitted &&
                                                !isChecked[uniqueId]
                                              }
                                            />
                                            {isChecked[uniqueId] && (
                                              <div>
                                                <button
                                                  className="button-size"
                                                  onClick={() =>
                                                    decrementAdicionesV2(
                                                      uniqueId
                                                    )
                                                  }
                                                >
                                                  −
                                                </button>
                                                <span
                                                  style={{
                                                    fontWeight: 600,
                                                    minWidth: "20px",
                                                    display: "inline-block",
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  {selectedQuantities[
                                                    uniqueId
                                                  ] || 1}
                                                </span>
                                                <button
                                                  className="button-size"
                                                  onClick={() =>
                                                    incrementAdicionesV2(
                                                      uniqueId,
                                                      group.quantity_max_permitted
                                                    )
                                                  }
                                                >
                                                  +
                                                </button>
                                              </div>
                                            )}
                                          </>
                                        );
                                      default:
                                        return null;
                                    }
                                  })()}
                                  <label htmlFor={bebida.app_item_id}>
                                    {acompanante
                                      ? acompanante.item_name
                                      : bebida.app_item_id}
                                  </label>
                                  <p>{formatCOP(price)}</p>
                                </div>
                              );
                            })}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}

                <div style={{ marginTop: "12px" }}>
                  <button
                    onClick={() => removeFromCart(item.app_item_id)}
                    aria-label="Quitar del carrito"
                  >
                    <FontAwesomeIcon icon={faTrash} /> Eliminar
                  </button>
                </div>
              </div>
            );
          })}

          {/* Cart Total */}
          {cart.length > 0 && (
            <>
              <div className="total-cart">
                <p>Total: {formattedTotal}</p>
              </div>
              {error && (
                <div
                  style={{
                    color: "var(--color-danger)",
                    fontSize: "var(--font-size-sm)",
                    fontWeight: 500,
                    textAlign: "center",
                    padding: "12px",
                    background: "rgba(239, 68, 68, 0.08)",
                    borderRadius: "8px",
                    marginBottom: "12px",
                  }}
                >
                  Debe elegir una opción en los campos requeridos.
                </div>
              )}
              <button className="checkout-btn" onClick={InitOrderTemporary}>
                <FontAwesomeIcon icon={faWhatsapp} />
                Iniciar pedido por WhatsApp
              </button>
            </>
          )}
        </Modal>
      </div>

      {/* ===== Pagination ===== */}
      {pageNumbers.length > 1 && (
        <div className="pagination">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ‹ Anterior
          </button>
          {pageNumbers.map((number) => (
            <button
              key={number}
              onClick={() => paginate(number)}
              className={currentPage === number ? "active" : ""}
            >
              {number}
            </button>
          ))}
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={
              currentPage ===
              Math.ceil(filteredProducts.length / productsPerPage)
            }
          >
            Siguiente ›
          </button>
        </div>
      )}
    </React.Fragment>
  );
};

export default ProductMenu;